import React, { useState, useEffect, useCallback } from 'react';
import API from '../../services/api';

const EditSaleModal = ({ sale, onClose, onRefresh }) => {
    const [selectedBike, setSelectedBike] = useState({
        id: sale?.bike_master_id || sale?.bike_id || '',
        model_name: sale?.model_name || 'N/A',
        engine_number: sale?.engine_number || 'N/A',
        chassis_number: sale?.chassis_number || 'N/A',
        reg_number: sale?.registration_no || sale?.reg_number || 'N/A'
    });

    const [selectedCust, setSelectedCust] = useState({
        id: sale?.customer_id || sale?.cust_id || '',
        full_name: sale?.customer_name || 'N/A',
        phone: sale?.customer_phone || sale?.phone || 'N/A'
    });

    const [step, setStep] = useState(1);
    const [accounts, setAccounts] = useState([]);
    const [taxRate, setTaxRate] = useState(0);

    const [regOption, setRegOption] = useState('existing');
    const [customRegNo, setCustomRegNo] = useState(sale?.registration_no || '');

    const [salePrice, setSalePrice] = useState(sale?.base_price || sale?.net_amount || '');
    const [saleType, setSaleType] = useState(sale?.sale_type || 'Full');
    const [totalMonths, setTotalMonths] = useState(sale?.total_months || 12);
    const [saleDate, setSaleDate] = useState(sale?.sale_date ? sale.sale_date.split('T')[0] : '');

    // 🔴 Fixed: Payments state initialized with a safe default row so it's never empty on load
    const [payments, setPayments] = useState([
        { method: 'Cash', account_id: '', amount: sale?.base_price || sale?.net_amount || '' }
    ]);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const fetchData = useCallback(async () => {
        try {
            const taxRes = await API.get('/v1/tax-settings');
            if (taxRes.data.success) setTaxRate(taxRes.data.percentage);

            const accRes = await API.get('/v1/purchase/payment-accounts');
            const accs = accRes.data && accRes.data.success ? (accRes.data.data || []) : [];
            setAccounts(accs);

            const saleRes = await API.get(`/v1/sales/${sale.id}/details`);
            if (saleRes.data && saleRes.data.success) {
                const freshSale = saleRes.data.data;
                
                setSelectedBike({
                    id: freshSale.bike_master_id || '',
                    model_name: freshSale.model_name || 'N/A',
                    engine_number: freshSale.engine_number || 'N/A',
                    chassis_number: freshSale.chassis_number || 'N/A',
                    reg_number: freshSale.registration_no || 'N/A'
                });

                setSelectedCust({
                    id: freshSale.customer_id || '',
                    full_name: freshSale.customer_name || 'N/A',
                    phone: freshSale.phone || 'N/A'
                });

                setSalePrice(freshSale.base_price || freshSale.net_amount || '');
                setSaleType(freshSale.sale_type || 'Full');
                setTotalMonths(freshSale.total_months || 12);
                setSaleDate(freshSale.sale_date ? freshSale.sale_date.split('T')[0] : '');

                // Payments mapping with accounts list
                if (freshSale.payments && freshSale.payments.length > 0) {
                    const mappedPayments = freshSale.payments.map(p => {
                        const matchedAcc = accs.find(a => String(a.id) === String(p.account_id));
                        const subHead = matchedAcc?.SubSubHeadName?.toLowerCase() || '';
                        const accTitle = matchedAcc?.title?.toLowerCase() || '';
                        const isBankAcc = subHead.includes('bank') || accTitle.includes('bank') || accTitle.includes('mcb') || accTitle.includes('hbl') || accTitle.includes('abl') || accTitle.includes('allied');

                        return {
                            method: isBankAcc ? 'Bank' : 'Cash',
                            account_id: p.account_id ? String(p.account_id) : (matchedAcc ? String(matchedAcc.id) : ''),
                            amount: p.amount || ''
                        };
                    });
                    setPayments(mappedPayments);
                } else {
                    setPayments([{ 
                        method: 'Cash', 
                        account_id: accs.length > 0 ? String(accs[0].id) : '', 
                        amount: freshSale.down_payment || freshSale.net_amount || '' 
                    }]);
                }
            }
        } catch (err) {
            console.error("Error loading sale details/accounts/tax:", err);
        }
    }, [sale]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const gstAmount = Math.round((parseFloat(salePrice || 0) * taxRate) / 100);
    const totalPrice = Math.round(parseFloat(salePrice || 0) + gstAmount);
    
    const totalPaid = payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
    const remainingBalance = Math.round(totalPrice - totalPaid);
    const monthlyAmount = totalMonths > 0 ? (remainingBalance / totalMonths) : 0;

    const addPaymentRow = () => {
        const defaultAcc = accounts.length > 0 ? String(accounts[0].id) : '';
        setPayments([...payments, { method: 'Cash', account_id: defaultAcc, amount: '' }]);
    };

    const removePaymentRow = (index) => {
        const list = [...payments];
        list.splice(index, 1);
        setPayments(list);
    };

    const handlePaymentChange = (index, field, value) => {
        const list = [...payments];
        list[index][field] = value;
        if (field === 'method') {
            const filteredAccs = accounts.filter(acc => {
                const subHead = acc.SubSubHeadName?.toLowerCase() || '';
                const accTitle = acc.title?.toLowerCase() || '';
                const isBank = subHead.includes('bank') || accTitle.includes('bank') || accTitle.includes('mcb') || accTitle.includes('hbl') || accTitle.includes('abl') || accTitle.includes('allied');
                return value === 'Bank' ? isBank : !isBank;
            });
            list[index]['account_id'] = filteredAccs.length > 0 ? String(filteredAccs[0].id) : '';
        }
        setPayments(list);
    };

    const handleFinalSubmit = async () => {
        if (!salePrice || !selectedBike.id || !selectedCust.id) {
            setError(`Missing required fields!`);
            return;
        }

        for (let p of payments) {
            if (parseFloat(p.amount || 0) > 0 && !p.account_id) {
                setError(`Please select an account for the ${p.method} payment row.`);
                return;
            }
        }

        setLoading(true);
        setError('');

        let finalRegNo = regOption === 'existing' ? selectedBike.reg_number : (regOption === 'new' ? customRegNo : 'Unregistered');

        const payload = {
            customer_id: selectedCust.id,
            bike_id: selectedBike.id,
            base_price: parseFloat(salePrice),
            down_payment: totalPaid,
            sale_type: saleType,
            total_months: saleType === 'Installment' ? parseInt(totalMonths) : 0,
            monthly_amount: saleType === 'Installment' ? Math.round(monthlyAmount) : 0,
            registration_no: finalRegNo,
            sale_date: saleDate || new Date().toISOString().split('T')[0],
            payments: payments.filter(p => parseFloat(p.amount || 0) > 0)
        };

        try {
            const res = await API.put(`/v1/sales/${sale.id}`, payload);
            if (res.data.success) {
                alert("✅ Sale updated successfully!");
                onRefresh();
                onClose();
            }
        } catch (err) {
            setError(err.response?.data?.error || "Failed to update sale record");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={styles.overlay}>
            <div style={styles.modal}>
                <button onClick={onClose} style={styles.closeBtn}>&times;</button>
                
                <h3 style={styles.mainHeading}>✏️ Edit Sale Record (Invoice / ID: {sale?.id})</h3>

                <div style={styles.stepper}>
                    <div style={step >= 1 ? styles.stepActive : styles.stepInactive}>1. Bike Info</div>
                    <div style={step >= 2 ? styles.stepActive : styles.stepInactive}>2. Customer Info</div>
                    <div style={step >= 3 ? styles.stepActive : styles.stepInactive}>3. Registration</div>
                    <div style={step >= 4 ? styles.stepActive : styles.stepInactive}>4. Billing & Payment</div>
                </div>

                {error && <div style={styles.errorBox}>{error}</div>}

                <div style={styles.body}>
                    {step === 1 && (
                        <div>
                            <h4 style={styles.title}>🏍️ Bike Specifications (Current)</h4>
                            <div style={styles.cardItemActive}>
                                <div>
                                    <strong style={{fontSize: '16px', color: '#1e293b'}}>{selectedBike.model_name}</strong>
                                    <div style={{fontSize:'13px', color:'#64748b', marginTop:'6px'}}>
                                        Engine #: <b>{selectedBike.engine_number}</b> | Chassis #: <b>{selectedBike.chassis_number}</b>
                                    </div>
                                </div>
                                <span style={styles.badgeFixed}>Locked / Current</span>
                            </div>
                            <div style={styles.footer}>
                                <div></div>
                                <button onClick={() => setStep(2)} style={styles.nextBtn}>Next Step &rarr;</button>
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div>
                            <h4 style={styles.title}>👤 Customer Information (Current)</h4>
                            <div style={styles.cardItemActive}>
                                <div>
                                    <strong style={{fontSize: '16px', color: '#1e293b'}}>{selectedCust.full_name}</strong>
                                    <div style={{fontSize:'13px', color:'#64748b', marginTop:'6px'}}>Phone: <b>{selectedCust.phone}</b> | ID: <b>{selectedCust.id}</b></div>
                                </div>
                                <span style={styles.badgeFixed}>Locked / Current</span>
                            </div>
                            <div style={styles.footer}>
                                <button onClick={() => setStep(1)} style={styles.backBtn}>&larr; Back</button>
                                <button onClick={() => setStep(3)} style={styles.nextBtn}>Next Step &rarr;</button>
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div>
                            <h4 style={styles.title}>🔢 Vehicle Registration Number</h4>
                            <div style={regOption === 'existing' ? styles.optionActive : styles.option} onClick={() => setRegOption('existing')}>
                                <strong>Keep Existing Number Plate:</strong> {selectedBike.reg_number}
                            </div>
                            <div style={regOption === 'new' ? styles.optionActive : styles.option} onClick={() => setRegOption('new')}>
                                <strong>Update/Register New Number Plate</strong>
                            </div>
                            {regOption === 'new' && (
                                <input 
                                    placeholder="Enter New Registration Number..." 
                                    value={customRegNo} 
                                    onChange={e => setCustomRegNo(e.target.value)} 
                                    style={styles.inputLarge} 
                                />
                            )}
                            <div style={styles.footer}>
                                <button onClick={() => setStep(2)} style={styles.backBtn}>&larr; Back</button>
                                <button onClick={() => setStep(4)} style={styles.nextBtn}>Next Step &rarr;</button>
                            </div>
                        </div>
                    )}

                    {step === 4 && (
                        <div>
                            <h4 style={styles.title}>💰 Billing & Split Payment Details</h4>

                            <div style={styles.formGrid}>
                                <div>
                                    <label style={styles.label}>Base Sale Price (PKR)</label>
                                    <input 
                                        type="number" 
                                        value={salePrice} 
                                        onChange={e => setSalePrice(e.target.value)} 
                                        style={styles.input} 
                                        required 
                                    />
                                </div>
                                <div>
                                    <label style={styles.label}>Sale Type</label>
                                    <select 
                                        value={saleType} 
                                        onChange={e => setSaleType(e.target.value)} 
                                        style={styles.input}
                                    >
                                        <option value="Full">Full Payment</option>
                                        <option value="Installment">Installment Plan</option>
                                    </select>
                                </div>
                            </div>

                            <div style={{marginBottom: '15px'}}>
                                <label style={styles.label}>Sale Date</label>
                                <input 
                                    type="date" 
                                    value={saleDate} 
                                    onChange={e => setSaleDate(e.target.value)} 
                                    style={styles.input} 
                                    required 
                                />
                            </div>

                            <div style={{ marginTop: '10px', marginBottom: '15px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                    <label style={{ fontWeight: 'bold', color: '#1e293b', fontSize: '13px' }}>Payment Breakdown (Cash / Bank Split)</label>
                                    <button type="button" onClick={addPaymentRow} style={styles.addPaymentBtn}>+ Add Row</button>
                                </div>

                                {payments.map((pay, index) => {
                                    const filteredAccounts = accounts.filter(acc => {
                                        const subHead = acc.SubSubHeadName?.toLowerCase() || '';
                                        const accTitle = acc.title?.toLowerCase() || '';
                                        const isBank = subHead.includes('bank') || accTitle.includes('bank') || accTitle.includes('mcb') || accTitle.includes('hbl') || accTitle.includes('abl') || accTitle.includes('allied');
                                        return pay.method === 'Bank' ? isBank : !isBank;
                                    });

                                    return (
                                        <div key={index} style={styles.paymentRow}>
                                            <select 
                                                value={pay.method} 
                                                onChange={e => handlePaymentChange(index, 'method', e.target.value)} 
                                                style={styles.paymentSelect}
                                            >
                                                <option value="Cash">Cash</option>
                                                <option value="Bank">Bank</option>
                                            </select>

                                            <select 
                                                value={pay.account_id} 
                                                onChange={e => handlePaymentChange(index, 'account_id', e.target.value)} 
                                                style={styles.paymentSelect}
                                            >
                                                <option value="">-- Select Account Title --</option>
                                                {filteredAccounts.map(acc => (
                                                    <option key={acc.id} value={String(acc.id)}>
                                                        {acc.SubSubHeadName ? `${acc.SubSubHeadName} ➔ ${acc.title}` : acc.title}
                                                    </option>
                                                ))}
                                            </select>

                                            <input 
                                                type="number" 
                                                placeholder="Amount" 
                                                value={pay.amount} 
                                                onChange={e => handlePaymentChange(index, 'amount', e.target.value)} 
                                                style={styles.paymentInput} 
                                            />

                                            {payments.length > 1 && (
                                                <button type="button" onClick={() => removePaymentRow(index)} style={styles.removeRowBtn}>&times;</button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            <div style={styles.summaryBox}>
                                <div style={styles.summaryRow}>
                                    <span>Base Price:</span>
                                    <strong>Rs. {parseFloat(salePrice || 0).toLocaleString()}</strong>
                                </div>
                                <div style={styles.summaryRow}>
                                    <span>GST ({taxRate}%):</span>
                                    <strong>Rs. {gstAmount.toLocaleString()}</strong>
                                </div>
                                <div style={{...styles.summaryRow, borderTop:'1px dashed #cbd5e1', paddingTop:'8px', marginTop:'4px'}}>
                                    <span style={{color:'#1e293b', fontWeight:'bold'}}>Total Net Payable:</span>
                                    <span style={{color:'#2563eb', fontWeight:'bold', fontSize:'15px'}}>Rs. {totalPrice.toLocaleString()}</span>
                                </div>
                                <div style={styles.summaryRow}>
                                    <span>Total Paid Now:</span>
                                    <span style={{color:'#16a34a', fontWeight:'bold'}}>Rs. {totalPaid.toLocaleString()}</span>
                                </div>
                                <div style={{...styles.summaryRow, background: remainingBalance > 0 ? '#fef2f2' : '#f0fdf4', padding:'8px', borderRadius:'6px', marginTop:'6px'}}>
                                    <span style={{fontWeight:'bold', color: remainingBalance > 0 ? '#dc2626' : '#16a34a'}}>Remaining Balance (Baqi):</span>
                                    <strong style={{color: remainingBalance > 0 ? '#dc2626' : '#16a34a'}}>Rs. {remainingBalance.toLocaleString()}</strong>
                                </div>
                            </div>

                            {saleType === 'Installment' && (
                                <div style={styles.installmentPanel}>
                                    <div style={{display:'flex', gap:'15px', alignItems:'center'}}>
                                        <label style={{fontSize:'13px', fontWeight:'bold'}}>Tenure (Months):</label>
                                        <input 
                                            type="number" 
                                            value={totalMonths} 
                                            onChange={e => setTotalMonths(e.target.value)} 
                                            style={{...styles.input, width:'80px', marginBottom:0}} 
                                        />
                                    </div>
                                    <p style={{marginTop:'8px', fontWeight:'bold', color:'#2563eb', fontSize:'13px'}}>Monthly Installment: Rs. {Math.round(monthlyAmount).toLocaleString()}</p>
                                </div>
                            )}

                            <div style={styles.footer}>
                                <button onClick={() => setStep(3)} style={styles.backBtn}>&larr; Back</button>
                                <button onClick={handleFinalSubmit} disabled={loading} style={styles.doneBtn}>
                                    {loading ? 'Updating...' : 'SAVE CHANGES ✅'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const styles = {
    overlay: { position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.85)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 5000, backdropFilter: 'blur(5px)' },
    modal: { background: '#fff', padding: '25px 30px', borderRadius: '16px', width: '95vw', height: '90vh', maxWidth: '950px', position: 'relative', boxShadow: '0 25px 35px -5px rgba(0,0,0,0.3)', display: 'flex', flexDirection: 'column' },
    mainHeading: { margin: '0 0 15px 0', fontSize: '18px', fontWeight: '700', color: '#1e293b' },
    body: { flex: 1, overflowY: 'auto', paddingRight: '5px' },
    closeBtn: { position: 'absolute', right: '20px', top: '20px', border: 'none', background: '#f1f5f9', width: '36px', height: '36px', borderRadius: '50%', cursor: 'pointer', fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' },
    stepper: { display: 'flex', justifyContent: 'space-around', marginBottom: '20px', borderBottom: '1px solid #f1f5f9', paddingBottom: '12px' },
    stepActive: { color: '#2563eb', fontWeight: 'bold', fontSize: '13px', borderBottom: '2px solid #2563eb', paddingBottom: '10px' },
    stepInactive: { color: '#94a3b8', fontSize: '13px' },
    title: { margin: '0 0 15px 0', color: '#1e293b', fontSize: '16px' },
    inputLarge: { width: '100%', padding: '12px', marginBottom: '15px', borderRadius: '8px', border: '1px solid #cbd5e1', outlineColor: '#2563eb', fontSize: '14px', boxSizing: 'border-box' },
    input: { width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', outlineColor: '#2563eb', boxSizing: 'border-box', fontSize: '13px' },
    label: { fontSize: '11px', color: '#64748b', marginBottom: '4px', display: 'block', fontWeight: 'bold', textTransform: 'uppercase' },
    cardItemActive: { padding: '16px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #cbd5e1', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
    badgeFixed: { background: '#eff6ff', color: '#2563eb', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold' },
    option: { padding: '14px', border: '1px solid #cbd5e1', borderRadius: '10px', marginBottom: '10px', cursor: 'pointer', background: '#fff', fontSize: '14px' },
    optionActive: { padding: '14px', border: '2px solid #2563eb', background: '#eff6ff', borderRadius: '10px', marginBottom: '10px', fontWeight: 'bold', fontSize: '14px' },
    formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '12px' },
    paymentRow: { display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'center' },
    paymentSelect: { flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', fontSize: '13px' },
    paymentInput: { width: '120px', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' },
    addPaymentBtn: { background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', padding: '5px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold' },
    removeRowBtn: { background: '#fee2e2', color: '#dc2626', border: 'none', width: '32px', height: '32px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '16px' },
    summaryBox: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px 15px', marginBottom: '12px' },
    summaryRow: { display: 'flex', justifyContent: 'space-between', marginBottom: '5px', fontSize: '13px', color: '#475569' },
    footer: { display: 'flex', justifyContent: 'space-between', marginTop: '20px', borderTop: '1px solid #f1f5f9', paddingTop: '15px' },
    nextBtn: { background: '#2563eb', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', fontSize: '13px' },
    backBtn: { background: '#f1f5f9', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', color: '#475569', fontWeight: 'bold', fontSize: '13px' },
    doneBtn: { background: '#10b981', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', fontSize: '14px' },
    installmentPanel: { background: '#f0fdf4', padding: '12px 15px', borderRadius: '10px', border: '1px solid #dcfce7', marginBottom: '12px' },
    errorBox: { background: '#fee2e2', color: '#b91c1c', padding: '10px', borderRadius: '8px', fontSize: '12px', marginBottom: '10px' }
};

export default EditSaleModal;